// Locale-stable date/time helper. We deliberately avoid toLocaleString()
// for admin tables because its DD/MM vs MM/DD output is ambiguous and
// makes "same instant" rows look like different days across pages.
//
// Output format: YYYY-MM-DD HH:MM (local timezone). Seconds are dropped
// because admin tables don't need sub-minute precision and trimming
// them keeps the column narrow.
export const formatDateTime = (input: string | number | Date | null | undefined): string => {
  if (input === null || input === undefined || input === "") return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
};

// Same as formatDateTime with seconds, for hover text where events seconds
// apart need telling apart.
export const formatDateTimeSeconds = (input: string | number | Date | null | undefined): string => {
  const base = formatDateTime(input);
  if (base === "" || input === null || input === undefined) return base;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? base : `${base}:${pad(d.getSeconds())}`;
};

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Compact form for dense admin tables: "17 Jun 15:21" (local timezone).
// Drops the year and zero-padding noise to keep the column to a single
// short line; the full YYYY-MM-DD HH:MM is surfaced on hover / in row
// detail so nothing is lost. Unambiguous across locales (named month).
export const formatDateTimeCompact = (input: string | number | Date | null | undefined): string => {
  if (input === null || input === undefined || input === "") return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  const dd = d.getDate();
  const mon = MONTHS[d.getMonth()];
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${dd} ${mon} ${hh}:${min}`;
};

// Compact relative form for "last seen" style columns: "just now",
// "5m ago", "3h ago", "12d ago", then falls back to the absolute date
// once it's far enough back that relative stops being useful.
export const formatRelativeTime = (input: string | number | Date | null | undefined): string => {
  if (input === null || input === undefined || input === "") return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 60) return `${days}d ago`;
  return formatDateTime(d).slice(0, 10);
};

const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
